import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, Observable } from 'rxjs';
import { CallError } from '../../types/telnyx-webrtc';

export interface WebRTCErrorDetails {
  code: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  userMessage: string;
  actionable: boolean;
  retryable: boolean;
  category: 'connection' | 'audio' | 'permission' | 'call' | 'authentication' | 'network';
  timestamp: Date;
  details?: any;
  suggestedActions?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class WebRTCErrorHandlerService {
  private errorSubject = new Subject<WebRTCErrorDetails>();
  private readonly errorCatalog = new Map<string, Omit<WebRTCErrorDetails, 'timestamp' | 'details'>>([
    // Connection Errors
    ['INIT_FAILED', {
      code: 'INIT_FAILED',
      message: 'Failed to initialize WebRTC client',
      severity: 'high',
      userMessage: 'Unable to initialize calling system. Please check your connection and try again.',
      actionable: true,
      retryable: true,
      category: 'connection',
      suggestedActions: ['Check internet connection', 'Refresh the page', 'Contact support if problem persists']
    }],
    ['CONNECTION_ERROR', {
      code: 'CONNECTION_ERROR',
      message: 'WebRTC connection error',
      severity: 'high',
      userMessage: 'Connection to calling service lost. Attempting to reconnect...',
      actionable: true,
      retryable: true,
      category: 'connection',
      suggestedActions: ['Check internet connection', 'Wait for automatic reconnection']
    }],
    ['AUTH_ERROR', {
      code: 'AUTH_ERROR',
      message: 'WebRTC authentication failed',
      severity: 'high',
      userMessage: 'Authentication failed. Please refresh the page and try again.',
      actionable: true,
      retryable: false,
      category: 'authentication',
      suggestedActions: ['Refresh the page', 'Re-login to your account']
    }],

    // Call Errors
    ['CALL_FAILED', {
      code: 'CALL_FAILED',
      message: 'Failed to initiate call',
      severity: 'medium',
      userMessage: 'Unable to start the call. Please try again.',
      actionable: true,
      retryable: true,
      category: 'call',
      suggestedActions: ['Verify phone number is correct', 'Check internet connection', 'Try again in a moment']
    }],
    ['ANSWER_FAILED', {
      code: 'ANSWER_FAILED',
      message: 'Failed to answer call',
      severity: 'medium',
      userMessage: 'Could not answer the incoming call.',
      actionable: false,
      retryable: false,
      category: 'call',
      suggestedActions: ['The caller may have hung up', 'Check your audio permissions']
    }],
    ['HANGUP_FAILED', {
      code: 'HANGUP_FAILED',
      message: 'Failed to hang up call',
      severity: 'low',
      userMessage: 'Unable to end the call properly. The call may have already ended.',
      actionable: false,
      retryable: false,
      category: 'call',
      suggestedActions: []
    }],

    // Audio Errors
    ['MIC_PERMISSION_DENIED', {
      code: 'MIC_PERMISSION_DENIED',
      message: 'Microphone permission denied',
      severity: 'critical',
      userMessage: 'Microphone access is required for calling. Please enable microphone permissions.',
      actionable: true,
      retryable: false,
      category: 'permission',
      suggestedActions: ['Enable microphone in browser settings', 'Reload the page after enabling permissions']
    }],
    ['AUDIO_DEVICE_ERROR', {
      code: 'AUDIO_DEVICE_ERROR',
      message: 'Audio device error',
      severity: 'medium',
      userMessage: 'There was an issue with your audio device. Please check your microphone and speakers.',
      actionable: true,
      retryable: true,
      category: 'audio',
      suggestedActions: ['Check microphone and speaker connections', 'Try different audio devices', 'Restart browser if needed']
    }],
    ['MUTE_FAILED', {
      code: 'MUTE_FAILED',
      message: 'Failed to toggle mute',
      severity: 'low',
      userMessage: 'Unable to mute/unmute. Please try again.',
      actionable: true,
      retryable: true,
      category: 'audio',
      suggestedActions: ['Try again', 'Check audio device settings']
    }],
    ['HOLD_FAILED', {
      code: 'HOLD_FAILED',
      message: 'Failed to toggle hold',
      severity: 'low',
      userMessage: 'Unable to put call on hold. Please try again.',
      actionable: true,
      retryable: true,
      category: 'call',
      suggestedActions: ['Try again', 'The call may have ended']
    }],

    // Device Errors
    ['DEVICE_ENUMERATION_FAILED', {
      code: 'DEVICE_ENUMERATION_FAILED',
      message: 'Failed to get audio devices',
      severity: 'medium',
      userMessage: 'Unable to detect audio devices. Using default settings.',
      actionable: true,
      retryable: true,
      category: 'audio',
      suggestedActions: ['Check device connections', 'Grant microphone permissions', 'Refresh the page']
    }],
    ['SET_DEVICE_FAILED', {
      code: 'SET_DEVICE_FAILED',
      message: 'Failed to set audio devices',
      severity: 'low',
      userMessage: 'Could not switch audio device. Using current device.',
      actionable: true,
      retryable: true,
      category: 'audio',
      suggestedActions: ['Try selecting device again', 'Check device is properly connected']
    }],

    // Network Errors
    ['NETWORK_ERROR', {
      code: 'NETWORK_ERROR',
      message: 'Network connectivity issue',
      severity: 'high',
      userMessage: 'Network connection issue detected. Call quality may be affected.',
      actionable: true,
      retryable: false,
      category: 'network',
      suggestedActions: ['Check internet connection', 'Move to area with better connectivity']
    }],
    ['DTMF_FAILED', {
      code: 'DTMF_FAILED',
      message: 'Failed to send DTMF',
      severity: 'low',
      userMessage: 'Unable to send phone keypad tones.',
      actionable: true,
      retryable: true,
      category: 'call',
      suggestedActions: ['Try again', 'Ensure call is active']
    }],

    // Browser Compatibility
    ['WEBRTC_NOT_SUPPORTED', {
      code: 'WEBRTC_NOT_SUPPORTED',
      message: 'WebRTC not supported in this browser',
      severity: 'critical',
      userMessage: 'Your browser does not support voice calling. Please use Chrome, Firefox, Safari, or Edge.',
      actionable: true,
      retryable: false,
      category: 'connection',
      suggestedActions: ['Use a supported browser (Chrome, Firefox, Safari, Edge)', 'Update your browser to the latest version']
    }],
    
    // Connection timeout errors
    ['CONNECTION_TIMEOUT', {
      code: 'CONNECTION_TIMEOUT',
      message: 'WebRTC connection timeout',
      severity: 'high',
      userMessage: 'Connection attempt timed out. This may be due to network issues or invalid credentials.',
      actionable: true,
      retryable: true,
      category: 'connection',
      suggestedActions: ['Check internet connection', 'Verify account credentials', 'Try again in a moment', 'Contact support if problem persists']
    }],
    
    // SIP registration errors
    ['SIP_REGISTRATION_ERROR', {
      code: 'SIP_REGISTRATION_ERROR',
      message: 'SIP registration failed',
      severity: 'high',
      userMessage: 'Failed to register with the calling service. Please check your account status.',
      actionable: true,
      retryable: true,
      category: 'authentication',
      suggestedActions: ['Verify account is active', 'Check calling permissions', 'Contact support if needed']
    }],
    
    // Backend registration warnings
    ['CONNECTION_REGISTRATION_WARNING', {
      code: 'CONNECTION_REGISTRATION_WARNING',
      message: 'WebRTC connection registration warning',
      severity: 'low',
      userMessage: 'Calling system is working but backend registration had issues.',
      actionable: false,
      retryable: false,
      category: 'connection',
      suggestedActions: []
    }]
  ]);

  constructor(private snackBar: MatSnackBar) {}

  /**
   * Handle WebRTC errors with appropriate user feedback and logging
   */
  handleError(error: CallError): void {
    const errorDetails = this.mapToErrorDetails(error);
    
    // Log error for debugging
    console.error('[WebRTC Error]', errorDetails);
    
    // Emit error for components to handle
    this.errorSubject.next(errorDetails);
    
    // Show user notification based on severity
    this.showUserNotification(errorDetails);
  }

  /**
   * Handle permission-related errors specifically
   */
  handlePermissionError(permissionType: 'microphone' | 'speaker' = 'microphone'): void {
    const errorDetails: WebRTCErrorDetails = {
      code: 'MIC_PERMISSION_DENIED',
      message: `${permissionType} permission denied`,
      severity: 'critical',
      userMessage: `${permissionType} access is required for calling. Please enable ${permissionType} permissions.`,
      actionable: true,
      retryable: false,
      category: 'permission',
      timestamp: new Date(),
      suggestedActions: [
        `Click the ${permissionType} icon in your browser's address bar`,
        'Select "Allow" for microphone/camera permissions',
        'Refresh the page after enabling permissions'
      ]
    };

    this.errorSubject.next(errorDetails);
    this.showPermissionDialog(errorDetails);
  }

  /**
   * Handle network connectivity issues
   */
  handleNetworkError(details?: any): void {
    const errorDetails: WebRTCErrorDetails = {
      code: 'NETWORK_ERROR',
      message: 'Network connectivity issue',
      severity: 'high',
      userMessage: 'Network connection issue detected. Call quality may be affected.',
      actionable: true,
      retryable: false,
      category: 'network',
      timestamp: new Date(),
      details,
      suggestedActions: ['Check internet connection', 'Move to area with better connectivity']
    };

    this.errorSubject.next(errorDetails);
    this.showNetworkWarning(errorDetails);
  }

  /**
   * Check browser compatibility and show warnings
   */
  checkBrowserCompatibility(): boolean {
    const isSupported = !!(
      navigator.mediaDevices &&
      navigator.mediaDevices.getUserMedia &&
      window.RTCPeerConnection
    );

    if (!isSupported) {
      this.handleError({
        code: 'WEBRTC_NOT_SUPPORTED',
        message: 'WebRTC not supported in this browser',
        details: { userAgent: navigator.userAgent }
      });
      return false;
    }

    // Check for older browsers with limited support
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
    const isEdge = /Edge/.test(navigator.userAgent);

    if (!isChrome && !isFirefox && !isSafari && !isEdge) {
      this.showWarning('Your browser may have limited calling features. For best experience, use Chrome, Firefox, Safari, or Edge.');
    }

    return true;
  }

  /**
   * Get observable for error events
   */
  getErrors(): Observable<WebRTCErrorDetails> {
    return this.errorSubject.asObservable();
  }

  /**
   * Show a general warning message
   */
  showWarning(message: string, duration: number = 5000): void {
    this.snackBar.open(message, 'OK', {
      duration,
      panelClass: 'warning-snackbar',
      horizontalPosition: 'center',
      verticalPosition: 'top'
    });
  }

  /**
   * Show success message
   */
  showSuccess(message: string, duration: number = 3000): void {
    this.snackBar.open(message, undefined, {
      duration,
      panelClass: 'success-snackbar',
      horizontalPosition: 'center',
      verticalPosition: 'bottom'
    });
  }

  /**
   * Map CallError to WebRTCErrorDetails
   */
  private mapToErrorDetails(error: CallError): WebRTCErrorDetails {
    const template = this.errorCatalog.get(error.code);
    
    if (template) {
      return {
        ...template,
        timestamp: new Date(),
        details: error.details
      };
    }

    // Fallback for unknown errors
    return {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      severity: 'medium',
      userMessage: 'An unexpected error occurred. Please try again.',
      actionable: true,
      retryable: true,
      category: 'connection',
      timestamp: new Date(),
      details: error.details,
      suggestedActions: ['Try again', 'Refresh the page if problem persists']
    };
  }

  /**
   * Show user notification based on error severity
   */
  private showUserNotification(errorDetails: WebRTCErrorDetails): void {
    const duration = this.getDurationBySeverity(errorDetails.severity);
    const panelClass = this.getPanelClassBySeverity(errorDetails.severity);
    
    const actionText = errorDetails.retryable ? 'Retry' : 
                      errorDetails.actionable ? 'Help' : 'OK';

    const snackBarRef = this.snackBar.open(
      errorDetails.userMessage,
      actionText,
      {
        duration,
        panelClass,
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    );

    // Handle action clicks
    if (errorDetails.actionable) {
      snackBarRef.onAction().subscribe(() => {
        if (errorDetails.suggestedActions && errorDetails.suggestedActions.length > 0) {
          this.showDetailedHelp(errorDetails);
        }
      });
    }
  }

  /**
   * Show permission dialog with detailed instructions
   */
  private showPermissionDialog(errorDetails: WebRTCErrorDetails): void {
    const actionList = errorDetails.suggestedActions?.join('\n• ') || '';
    
    this.snackBar.open(
      `${errorDetails.userMessage}\n\nTo enable permissions:\n• ${actionList}`,
      'I understand',
      {
        duration: 0, // Don't auto-dismiss
        panelClass: 'critical-error-snackbar',
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    );
  }

  /**
   * Show network warning with minimal disruption
   */
  private showNetworkWarning(errorDetails: WebRTCErrorDetails): void {
    this.snackBar.open(
      errorDetails.userMessage,
      'OK',
      {
        duration: 8000,
        panelClass: 'network-warning-snackbar',
        horizontalPosition: 'right',
        verticalPosition: 'bottom'
      }
    );
  }

  /**
   * Show detailed help for actionable errors
   */
  private showDetailedHelp(errorDetails: WebRTCErrorDetails): void {
    const helpText = errorDetails.suggestedActions?.join('\n• ') || 'No specific help available.';
    
    this.snackBar.open(
      `Troubleshooting steps:\n• ${helpText}`,
      'Got it',
      {
        duration: 15000,
        panelClass: 'help-snackbar',
        horizontalPosition: 'center',
        verticalPosition: 'top'
      }
    );
  }

  /**
   * Get snackbar duration based on error severity
   */
  private getDurationBySeverity(severity: WebRTCErrorDetails['severity']): number {
    switch (severity) {
      case 'low': return 3000;
      case 'medium': return 5000;
      case 'high': return 8000;
      case 'critical': return 0; // Don't auto-dismiss critical errors
      default: return 5000;
    }
  }

  /**
   * Get snackbar panel class based on error severity
   */
  private getPanelClassBySeverity(severity: WebRTCErrorDetails['severity']): string {
    switch (severity) {
      case 'low': return 'info-snackbar';
      case 'medium': return 'warning-snackbar';
      case 'high': return 'error-snackbar';
      case 'critical': return 'critical-error-snackbar';
      default: return 'warning-snackbar';
    }
  }

  /**
   * Clear all error notifications
   */
  clearNotifications(): void {
    this.snackBar.dismiss();
  }
}